import React, { useEffect, useMemo, useState } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { toast } from 'react-toastify';
import ApiBaseURL from '../ApiBaseUrl/ApiBaseURL';
import { useNavigate } from 'react-router-dom';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const DashboardTripFuelChart = ({ period }) => {

    const [selectedRange, setSelectedRange] = useState('3m');
    const [showDropdown, setShowDropdown] = useState(false);
    const [fuelStocks, setFuelStocks] = useState({});

    const navigate = useNavigate();

    const monthNames = [
        '', 'Jan-25', 'Feb-25', 'Mar-25', 'Apr-5', 'May-25',
        'Jun-25', 'Jul-25', 'Aug-25', 'Sep-25', 'Oct-25', 'Nov-25', 'Dec-25'
    ];

    const filterOptions = [
        { label: 'Year-25', value: 'y-25' },
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Week', value: 'week' },
        { label: 'Current Month', value: 'cm' },
        { label: 'Prev. Month', value: 'pm' },
        { label: '3M', value: '3m' },
        { label: '6M', value: '6m' },
        { label: '9M', value: '9m' },
    ];

    const handleGetFuelStocks = async () => {
        const userId = localStorage.getItem('userId');
        const token = localStorage.getItem('token');
        if (!userId || !token) return toast.error('Missing userId or token');

        try {
            const resp = await fetch(`${ApiBaseURL}/dashboard/v1/get/fuel-stock-summary`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    userId,
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    // period: selectedRange
                    period
                })
            });

            const result = await resp.json();
            if (resp.ok && result.statusDescription?.statusCode === 200) {
                setFuelStocks(result?.data || {});
            } else if ([401, 403].includes(result.statusDescription.statusCode)) {
                toast.error(result.statusDescription.description || "Unauthorized access. Redirecting...");
                localStorage.clear();

                setTimeout(() => {
                    window.location.replace('/');
                }, 1500);
            } else {
                console.log(result.statusDescription?.description || 'Failed to fetch data');
            }
        } catch (err) {
            toast.error('Fetch error: ' + err.message);
        }
    };

    useEffect(() => {
        handleGetFuelStocks();
    }, [selectedRange, period]);

    const parsed = Object.entries(fuelStocks)
        .map(([key, item]) => {
            const isDate = /^\d{4}-\d{2}-\d{2}$/.test(key);
            const label = isDate
                ? new Date(key).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
                : monthNames[parseInt(key)] || key;
            const sortKey = isDate
                ? new Date(key).getTime()
                : parseInt(key);

            return {
                label,
                sortKey,
                fuelQuantity: item.fuelQuantity || 0,
                composite: item.composite || 0,
                fuelTotalAmount: item.fuelTotalAmount || 0
            };
        })
        .sort((a, b) => a.sortKey - b.sortKey);

    const labels = parsed.map(p => p.label);
    const fuelQuantity = parsed.map(p => Math.round(p.fuelQuantity));
    const composite = parsed.map(p => Math.round(p.composite));
    const fuelTotalAmount = parsed.map(p => Math.round(p.fuelTotalAmount));

    const data = {
        labels,
        datasets: [
            {
                label: 'Stock Added (Ltr.)',
                data: fuelQuantity,
                borderColor: 'rgb(14, 115, 115)',
                backgroundColor: 'rgba(75,192,192,0.2)',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: 'Stock Consumed (Ltr.)',
                data: composite,
                borderColor: 'rgb(184, 98, 12)',
                backgroundColor: 'rgba(255,159,64,0.2)',
                tension: 0.4,
                borderWidth: 1
            },
            {
                label: 'Remaining Stock (Ltr.)',
                data: fuelTotalAmount,
                borderColor: 'rgb(107, 55, 213)',
                backgroundColor: 'rgba(153,102,255,0.2)',
                tension: 0.4,
                borderWidth: 1
            }
        ]
    };

    const options = {
        responsive: true,
        plugins: {
            legend: { position: 'top' },
            // title: {
            //     display: true,
            //     text: /^\d+$/.test(Object.keys(fuelStocks)[0])
            //         ? 'Fuel Stocks'
            //         : 'Fuel Stocks'
            // }
        },
        scales: {
            y: {
                ticks: {
                    callback: function (value) {
                        if (value >= 1_00_00_000) return (value / 1_00_00_000).toFixed(1).replace(/\.0$/, '') + 'Cr';
                        if (value >= 1_00_000) return (value / 1_00_000).toFixed(1).replace(/\.0$/, '') + 'L';
                        if (value >= 1_000_000) return (value / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
                        if (value >= 1_000) return (value / 1_000).toFixed(1).replace(/\.0$/, '') + 'K';
                        return value;
                    }
                }
            }
        }
    };

    const handleFilterSelect = (value) => {
        setSelectedRange(value);
        setShowDropdown(false);
    };

    const totals = useMemo(() => {
        return Object.values(fuelStocks).reduce(
            (acc, item) => {
                acc.fuelQuantity += item?.fuelQuantity || 0;
                acc.composite += item?.composite || 0;
                acc.fuelTotalAmount += item?.fuelTotalAmount || 0;
                return acc;
            },
            { fuelQuantity: 0, composite: 0, fuelTotalAmount: 0 }
        );
    }, [fuelStocks]);

    return (
        <div className="card shadow-sm my-0" style={{ maxWidth: '800px', margin: 'auto' }}>
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">Fuel Stock Summary</h5>
                    <div style={{ position: 'relative' }}>
                        <i
                            className="fa-solid fa-filter d-none"
                            style={{ cursor: 'pointer', fontSize: '18px' }}
                            onClick={() => setShowDropdown(!showDropdown)}
                        ></i>
                        {showDropdown && (
                            <div className="dropdown-menu show" style={{ position: 'absolute', right: 0, zIndex: 999 }}>
                                {filterOptions.map(opt => (
                                    <button
                                        key={opt.value}
                                        className="dropdown-item"
                                        onClick={() => handleFilterSelect(opt.value)}
                                    >
                                        {opt.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <Line data={data} options={options} />
            </div>

            <div className="row mb-3 text-center">
                <div className="col-md-4">
                    <h6 className="mb-1">Stock Added (Ltr.)</h6>
                    <p className="fw-bold text-primary mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.fuelQuantity))}
                    </p>
                </div>

                <div className="col-md-4">
                    <h6 className="mb-1">Stock Consumed (Ltr.)</h6>
                    <p className="fw-bold text-success mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.composite))}
                    </p>
                </div>

                <div className="col-md-4">
                    <h6 className="mb-1">Remaining Stock (Ltr.)</h6>
                    <p className="fw-bold text-warning mb-0">
                        {new Intl.NumberFormat('en-IN').format(Math.round(totals.fuelTotalAmount))}
                    </p>
                </div>
            </div>

        </div>
    );
};

export default DashboardTripFuelChart;